import {
    Button,
    Icon,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronDown, TbHistory, TbPhone } from "react-icons/tb";

import { HeaderCallDialerContent } from "@/components/header/call-dialer-content";
import {
    HEADER_ACTION_BUTTON_CLASS,
    HEADER_LAST_CALL_POPOVER_CLASS,
} from "@/components/header/constants/header-controls";
import { HeaderCallDialerVariant } from "@/components/header/types/call-dialer.types";
import { useLastCallRecord } from "@/hooks/use-last-call-record";
import { useCall } from "@/providers/hooks/use-call";
import { getDisplayName, getPrimaryPhoneNumber } from "@/utils/crm-module.utils";

function formatLastCallTime(calledAt: string | undefined): string | null {
    if (!calledAt) {
        return null;
    }

    const dateTime = DateTime.fromISO(calledAt);
    if (!dateTime.isValid) {
        return null;
    }

    return dateTime.toFormat("HH:mm");
}

export function HeaderLastCallPopover() {
    const { t } = useTranslation();
    const { callButtonCooldown, isCallActive } = useCall();
    const { lastCallRecord, callLastRecord } = useLastCallRecord();
    const [open, setOpen] = useState(false);

    const name = lastCallRecord ? getDisplayName(lastCallRecord.data, lastCallRecord.moduleType) : "";
    const phoneNumber = lastCallRecord ? getPrimaryPhoneNumber(lastCallRecord.data, lastCallRecord.moduleType) : null;
    const time = formatLastCallTime(lastCallRecord?.calledAt);
    const canCall = Boolean(phoneNumber) && !callButtonCooldown && !isCallActive();

    return (
        <div className="flex h-8 items-center">
            {lastCallRecord ? (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <Button
                                variant="ghost"
                                className="h-8 max-w-52 gap-1.5 px-2 text-sm font-medium"
                                onClick={() => {
                                    if (phoneNumber) {
                                        callLastRecord(phoneNumber);
                                    }
                                }}
                                disabled={!canCall}
                            >
                                <span className="truncate">{name}</span>
                                <Icon icon={TbPhone} />
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={5}>
                        {time ? t("click_to_call_last_call_today", { time }) : t("last_call")}
                    </TooltipContent>
                </Tooltip>
            ) : null}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={lastCallRecord ? "ghost" : "default"}
                        className={lastCallRecord ? "h-8 w-8 px-0" : HEADER_ACTION_BUTTON_CLASS}
                        aria-label={t("last_call")}
                    >
                        {lastCallRecord ? (
                            <Icon icon={TbChevronDown} />
                        ) : (
                            <>
                                <Icon icon={TbHistory} />
                                <span className="hidden lg:inline">{t("last_call")}</span>
                            </>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={8} className={HEADER_LAST_CALL_POPOVER_CLASS}>
                    <p className="mb-2 text-sm font-medium">{t("last_call")}</p>
                    <HeaderCallDialerContent variant={HeaderCallDialerVariant.LAST_CALL} />
                </PopoverContent>
            </Popover>
        </div>
    );
}
